import { useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAgentStore, useChatStore, useCoworkerStore } from '@/store';
import { findTaskIdForConversation } from '@/store/coworker';

const EMPTY_IDS = [];
const inFlightAgents = new Map();

function agentId(value) {
  if (typeof value === 'string') return value || null;
  if (value && typeof value === 'object') return agentId(value.id || value._id);
  return null;
}

export function resolveMessageAgentIds(message, { taskAgentId, conversationAgentId } = {}) {
  if (message?.role !== 'assistant') return EMPTY_IDS;
  const selected = agentId(taskAgentId);
  if (selected) return [selected];
  const explicit = Array.isArray(message.agentIds) ? message.agentIds.map(agentId).filter(Boolean) : EMPTY_IDS;
  if (explicit.length) return explicit;
  const fallback = agentId(conversationAgentId);
  return fallback ? [fallback] : EMPTY_IDS;
}

function useConversationAgentIdentity(conversationId) {
  const conversationAgentId = useChatStore(state => agentId(state.chats.find(c => c.id === conversationId)?.agentId));
  const coworkerSession = useChatStore(state => state.chats.find(c => c.id === conversationId)?.coworkerSession);
  const taskAgentId = useCoworkerStore(state => {
    const taskId = findTaskIdForConversation(conversationId, {
      chat: { coworkerSession }, tasks: state.tasks, taskIds: state.taskIds,
    });
    const task = state.tasks[taskId] || (typeof coworkerSession === 'object' ? coworkerSession : null);
    return agentId(task?.context?.agentId) || agentId(task?.agentId);
  });
  return { taskAgentId, conversationAgentId };
}

/** Response rows subscribe to metadata, but never fetch it. */
export default function useMessageAgents(conversationId, { role, agentIds }) {
  const { taskAgentId, conversationAgentId } = useConversationAgentIdentity(conversationId);
  const ids = useMemo(() => resolveMessageAgentIds({ role, agentIds }, { taskAgentId, conversationAgentId }),
    [role, agentIds, taskAgentId, conversationAgentId]);
  const cached = useAgentStore(useShallow(state => ids.map(id => state.agents[id])));
  const agents = useMemo(() => ids.map((id, index) => cached[index] || { id }), [ids, cached]);
  return { agentIds: ids, agents };
}

/** One owner per conversation, including Work drafts which have no message.agentIds. */
export function useConversationAgentHydration(conversationId) {
  const { taskAgentId, conversationAgentId } = useConversationAgentIdentity(conversationId);
  const messageAgentIds = useChatStore(useShallow(state => {
    const chat = state.chats.find(c => c.id === conversationId);
    return [...new Set((chat?.messages || []).flatMap(message => resolveMessageAgentIds(message)))];
  }));
  const attempted = useRef({ conversationId, ids: new Set() });
  useEffect(() => {
    if (attempted.current.conversationId !== conversationId) {
      attempted.current = { conversationId, ids: new Set() };
    }
    const ids = taskAgentId ? [taskAgentId] : [conversationAgentId, ...messageAgentIds].filter(Boolean);
    for (const id of ids) {
      if (useAgentStore.getState().agents[id] || attempted.current.ids.has(id)) continue;
      attempted.current.ids.add(id);
      // StrictMode and overlapping ChatViews can share a request without each
      // response issuing a fetch before the first one populates the store.
      if (!inFlightAgents.has(id)) {
        const request = Promise.resolve().then(() => useAgentStore.getState().fetchAgentById(id));
        inFlightAgents.set(id, request);
        request.catch(() => {}).finally(() => inFlightAgents.delete(id));
      }
    }
  }, [conversationId, conversationAgentId, taskAgentId, messageAgentIds]);
}
